import { priceForCm, selectLadderVariant, validateCustomDimensions, woolPercentage } from './pdp-pricing.js';
import { bindFramePicker, customFrameSurcharge } from './pdp-frame-picker.js?v=1789277000';

export function priceForDimensions(widthFeet, widthInches, lengthFeet, lengthInches) {
  const widthMetres = (Number(widthFeet) * 12 + Number(widthInches)) * 0.0254;
  const lengthMetres = (Number(lengthFeet) * 12 + Number(lengthInches)) * 0.0254;
  const area = widthMetres * lengthMetres;
  return Math.round(area < 1 ? 210 : area * 220);
}

export function mediaColour(alt) {
  const parts = String(alt ?? '').split('::');
  return parts.length > 2 && parts[0] === 'HEWN' ? parts[1] : '';
}

export function accessibleMediaAlt(alt) {
  const parts = String(alt ?? '').split('::');
  return parts.length > 2 && parts[0] === 'HEWN' ? parts.at(-1) : String(alt ?? '');
}

export function findVariant(variants, optionNames, selections) {
  return variants.find((variant) =>
    optionNames.every((name, index) => variant.options[index] === selections[name]),
  ) ?? null;
}

export function variantOptionForSelections(options, selections) {
  return [...options].find((option) =>
    Object.entries(selections).every(([key, value]) => !value || option.dataset[key] === value),
  );
}

export function variantOptionForChoice(options, currentOption, choiceName, choiceValue) {
  if (!['colour', 'size', 'material', 'frame'].includes(choiceName)) return null;
  return variantOptionForSelections(options, {
    colour: currentOption?.dataset.colour ?? '',
    size: currentOption?.dataset.size ?? '',
    material: currentOption?.dataset.material ?? '',
    frame: currentOption?.dataset.frame ?? '',
    [choiceName]: choiceValue,
  }) ?? null;
}

export function dualUnitSizeLabel(sizeValue) {
  const source = String(sizeValue ?? '').trim();
  const match = source.match(/^(.+?)\s*·\s*(\d+(?:\.\d+)?)\s*×\s*(\d+(?:\.\d+)?)\s*cm$/i);
  if (!match) return source;
  const [, code, widthCm, heightCm] = match;
  const inches = (value) => (Number(value) * 0.3937008).toFixed(1);
  return `${code.trim()} · ${inches(widthCm)} × ${inches(heightCm)} in / ${widthCm} × ${heightCm} cm`;
}

function selectedOption(select) {
  return select?.options[select.selectedIndex] ?? null;
}

function filterGallery(root, colour) {
  const media = [...root.querySelectorAll('[data-product-media]')];
  const hasMatches = media.some((item) => item.dataset.mediaColour === colour);
  for (const item of media) {
    item.hidden = hasMatches && item.dataset.mediaColour !== '' && item.dataset.mediaColour !== colour;
  }
}

function bindSizeGuide(root) {
  const drawer = document.querySelector('[data-size-guide]');
  const openers = [...root.querySelectorAll('[data-size-guide-open]')];
  if (!drawer || openers.length === 0) return;
  let lastOpener = openers[0];

  const close = () => {
    drawer.hidden = true;
    document.body.style.overflow = '';
    lastOpener.focus();
  };
  openers.forEach((opener) => opener.addEventListener('click', () => {
      lastOpener = opener;
      drawer.hidden = false;
      document.body.style.overflow = 'hidden';
      drawer.querySelector('[data-size-guide-close]')?.focus();
    }));
  drawer.querySelectorAll('[data-size-guide-close]').forEach((button) => button.addEventListener('click', close));
  drawer.querySelectorAll('[data-guide-tab]').forEach((button) => button.addEventListener('click', () => {
    drawer.querySelectorAll('[data-guide-tab]').forEach((tab) => tab.classList.toggle('is-active', tab === button));
    drawer.querySelectorAll('[data-guide-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.guidePanel !== button.dataset.guideTab;
    });
  }));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !drawer.hidden) close();
  });
}

function bindCustomEstimate(root) {
  const customForm = root.querySelector('[data-custom-size-form]');
  if (!customForm) return () => {};
  const widthFeet = customForm.querySelector('[data-custom-width-feet]');
  const widthInches = customForm.querySelector('[data-custom-width-inches]');
  const lengthFeet = customForm.querySelector('[data-custom-length-feet]');
  const lengthInches = customForm.querySelector('[data-custom-length-inches]');
  const areaOutput = customForm.querySelector('[data-custom-area]');
  const priceOutput = customForm.querySelector('[data-custom-price]');
  const quote = customForm.querySelector('[data-custom-quote]');

  const update = () => {
    const widthTotal = Number(widthFeet.value) * 12 + Number(widthInches.value);
    const lengthTotal = Number(lengthFeet.value) * 12 + Number(lengthInches.value);
    const area = (widthTotal * 0.0254) * (lengthTotal * 0.0254);
    const price = priceForDimensions(widthFeet.value, widthInches.value, lengthFeet.value, lengthInches.value);
    areaOutput.textContent = area.toFixed(2);
    priceOutput.textContent = `$${price.toLocaleString('en-US')}`;
    const subject = `Custom rug size quote — ${widthFeet.value}' ${widthInches.value}\" × ${lengthFeet.value}' ${lengthInches.value}\"`;
    quote.href = `mailto:hello@hewnearth.com?subject=${encodeURIComponent(subject)}`;
  };
  customForm.querySelectorAll('select').forEach((select) => select.addEventListener('change', update));
  update();
  return update;
}

function bindStickyPanel(root) {
  const panel = root.querySelector('.he-pdp__panel');
  if (!panel) return;
  const update = () => {
    if (window.matchMedia('(max-width: 959px)').matches) {
      panel.style.removeProperty('--pdp-sticky-top');
      return;
    }
    const headroom = 88;
    const overflow = panel.offsetHeight - (window.innerHeight - headroom);
    panel.style.setProperty('--pdp-sticky-top', `${overflow > 0 ? -(overflow + 24) : headroom}px`);
  };
  window.addEventListener('resize', update, { passive: true });
  if (typeof ResizeObserver !== 'undefined') new ResizeObserver(update).observe(panel);
  update();
}

export function updateBundleOffer(root, option) {
  const offer = root.querySelector('[data-bundle-offer]');
  if (!offer) return;
  const two = option?.dataset.bundleTwoPrice;
  const three = option?.dataset.bundleThreePrice;
  offer.hidden = !two || !three;
  if (offer.hidden) return;
  offer.querySelector('[data-bundle-two-price]').textContent = two;
  offer.querySelector('[data-bundle-three-price]').textContent = three;
}

function initialiseProduct(root) {
  if (root.dataset.pdpBound === 'true') return;
  root.dataset.pdpBound = 'true';
  const select = root.querySelector('[data-variant-select]');
  const price = root.querySelector('[data-product-price]');
  const paymentTermsVariant = root.querySelector('[data-payment-terms-variant]');
  const submit = root.querySelector('[data-add-to-cart]');
  const customForm = root.querySelector('[data-custom-size-form]');
  const enhancedPicker = root.querySelector('[data-enhanced-variant-picker]');
  const activeColourOutput = root.querySelector('[data-active-colour]');
  const activeMaterialOutput = root.querySelector('[data-active-material]');
  const activeSizeOutput = root.querySelector('[data-active-size]');
  const chips = [...root.querySelectorAll('[data-colour-value]')];
  const materialButtons = [...root.querySelectorAll('[data-material-value]')];
  const sizeButtons = [...root.querySelectorAll('[data-size-value]')];
  const wishlist = root.querySelector('[data-wishlist]');
  bindCustomEstimate(root);
  bindSizeGuide(root);
  bindStickyPanel(root);
  const pricingControls = bindPricingPicker(root, select, submit);
  const frameControls = bindFramePicker(root);
  const frameButtons = frameControls?.buttons ?? [];

  function syncColourUi(colour) {
    if (!colour) return;
    chips.forEach((chip) => {
      const selected = chip.dataset.colourValue === colour;
      chip.classList.toggle('is-selected', selected);
      chip.setAttribute('aria-pressed', String(selected));
    });
    if (activeColourOutput) activeColourOutput.textContent = colour;
    root.dataset.activeColour = colour;
    filterGallery(root, colour);
  }

  function syncMaterialUi(material) {
    if (!material) return;
    materialButtons.forEach((button) => {
      const selected = button.dataset.materialValue === material;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    if (activeMaterialOutput) activeMaterialOutput.textContent = material;
    root.dataset.activeMaterial = material;
  }

  function syncSizeUi(size) {
    if (!size) return;
    sizeButtons.forEach((button) => {
      const selected = button.dataset.sizeValue === size;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    if (activeSizeOutput) activeSizeOutput.textContent = dualUnitSizeLabel(size);
    root.dataset.activeSize = size;
  }

  function syncFrameUi(frame, size) {
    if (!frame || !frameControls) return;
    frameControls.sync(frame, size ?? selectedOption(select)?.dataset.size);
  }

  function syncChoiceAvailability(current) {
    materialButtons.forEach((button) => {
      const target = variantOptionForChoice(select.options, current, 'material', button.dataset.materialValue);
      button.disabled = !target || target.dataset.available !== 'true';
    });
    sizeButtons.forEach((button) => {
      const target = variantOptionForChoice(select.options, current, 'size', button.dataset.sizeValue);
      button.disabled = !target || target.dataset.available !== 'true';
    });
    frameButtons.forEach((button) => {
      const target = variantOptionForChoice(select.options, current, 'frame', button.dataset.frameValue);
      button.disabled = !target || target.dataset.available !== 'true';
    });
  }

  const setColour = (colour) => {
    if (!colour || !select) return;
    const current = selectedOption(select);
    const target = variantOptionForSelections(select.options, {
      colour,
      size: current?.dataset.size,
      material: current?.dataset.material,
      frame: current?.dataset.frame,
    }) ?? variantOptionForSelections(select.options, {
      colour,
      material: current?.dataset.material,
    }) ?? variantOptionForSelections(select.options, { colour });
    if (!target) return;
    select.value = target.value;
  };

  const setChoice = (choiceName, choiceValue) => {
    if (!choiceValue || !select) return false;
    const target = variantOptionForChoice(
      select.options,
      selectedOption(select),
      choiceName,
      choiceValue,
    );
    if (!target || target.dataset.available !== 'true') return false;
    select.value = target.value;
    return true;
  };

  const syncPricingChoice = (choice, value) => pricingControls?.syncPricingChoice(choice, value);
  const setMaterial = (material) => {
    const selected = setChoice('material', material);
    if (selected) syncPricingChoice('material', material);
    return selected;
  };
  const setSize = (size) => {
    const selected = setChoice('size', size);
    if (selected) syncPricingChoice('size', size);
    return selected;
  };
  const setFrame = (frame) => {
    const selected = setChoice('frame', frame);
    if (selected) syncPricingChoice('frame', frame);
    return selected;
  };

  const updateVariant = () => {
    const option = selectedOption(select);
    const isCustom = option?.hasAttribute('data-custom-size') || select.value === 'custom';
    const hasCustomDimensions = root.querySelector('[data-pricing-size]')?.value === 'Custom size';
    updateBundleOffer(root, isCustom || hasCustomDimensions ? null : option);
    if (customForm) customForm.hidden = !isCustom;
    if (submit) submit.hidden = isCustom;
    if (isCustom || !option) return;

    if (option.dataset.colour) syncColourUi(option.dataset.colour);
    if (option.dataset.material) syncMaterialUi(option.dataset.material);
    if (option.dataset.size) syncSizeUi(option.dataset.size);
    if (option.dataset.frame) syncFrameUi(option.dataset.frame, option.dataset.size);
    syncChoiceAvailability(option);
    if (price) price.textContent = option.dataset.price;
    if (paymentTermsVariant) {
      paymentTermsVariant.value = option.value;
      paymentTermsVariant.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (submit) {
      submit.disabled = option.dataset.available !== 'true';
      submit.textContent = submit.disabled ? 'Sold out' : 'Add to cart';
    }
    if (wishlist) wishlist.dataset.variantId = option.value;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', option.value);
    window.history.replaceState({}, '', url);
  };

  chips.forEach((chip) => chip.addEventListener('click', () => {
    setColour(chip.dataset.colourValue);
    updateVariant();
  }));
  materialButtons.forEach((button) => button.addEventListener('click', () => {
    if (setMaterial(button.dataset.materialValue)) updateVariant();
  }));
  sizeButtons.forEach((button) => button.addEventListener('click', () => {
    if (setSize(button.dataset.sizeValue)) updateVariant();
  }));
  frameButtons.forEach((button) => button.addEventListener('click', () => {
    if (setFrame(button.dataset.frameValue)) updateVariant();
  }));
  root.querySelector('[data-custom-pricing-open]')?.addEventListener('click', () => pricingControls?.openCustomSize());
  wishlist?.addEventListener('click', () => {
    const selected = wishlist.getAttribute('aria-pressed') === 'true';
    wishlist.setAttribute('aria-pressed', String(!selected));
    wishlist.classList.toggle('is-selected', !selected);
    const label = wishlist.querySelector('[data-wishlist-label]');
    if (label) label.textContent = selected ? 'Add to Wishlist' : 'Saved to Wishlist';
  });
  select?.addEventListener('change', updateVariant);
  if (enhancedPicker && select && materialButtons.length > 0 && sizeButtons.length > 0) {
    root.classList.add('is-variant-picker-ready');
  }
  updateVariant();
}

function bindPricingPicker(root, nativeSelect, submit) {
  const picker = root.querySelector('[data-pricing-picker]');
  const pricingNode = root.querySelector('[data-pdp-pricing]');
  if (!picker || !pricingNode || !nativeSelect || !submit) return null;
  const pricing = JSON.parse(pricingNode.textContent);
  const size = picker.querySelector('[data-pricing-size]');
  const wool = picker.querySelector('[data-pricing-wool]');
  const frame = picker.querySelector('[data-pricing-frame]');
  const custom = picker.querySelector('[data-custom-size]');
  const width = picker.querySelector('[data-custom-width-cm]');
  const height = picker.querySelector('[data-custom-height-cm]');
  const error = picker.querySelector('[data-pricing-error]');
  const area = picker.querySelector('[data-pricing-area]');
  const price = picker.querySelector('[data-pricing-price]');
  const lead = picker.querySelector('[data-pricing-lead]');
  const property = (name) => picker.querySelector(`[name="properties[_${name}]"]`);
  const isCustom = () => size.value === 'Custom size';
  const setProperty = (name, value) => { const input = property(name); if (input) input.value = value; };
  const update = () => {
    custom.hidden = !isCustom();
    if (isCustom()) updateBundleOffer(root, null);
    if (!isCustom()) {
      const option = [...nativeSelect.options].find((item) => item.dataset.size === size.value
        && item.dataset.material === wool.value
        && (!frame || item.dataset.frame === frame.value));
      if (option) { nativeSelect.value = option.value; nativeSelect.dispatchEvent(new Event('change')); }
      return;
    }
    const valid = validateCustomDimensions(width.value, height.value, pricing);
    error.textContent = valid.error;
    submit.disabled = !valid.valid;
    if (!valid.valid) { price.textContent = 'Enter valid dimensions'; return; }
    const result = priceForCm(height.value, width.value, woolPercentage(wool.value), pricing.complexity, pricing);
    const framePremium = frame ? customFrameSurcharge(width.value, height.value, frame.value) : 0;
    if (framePremium === null) { error.textContent = 'Choose a valid frame.'; submit.disabled = true; return; }
    result.price += framePremium;
    result.price = Math.max(result.price, Number(pricing.customMinPrice || 0));
    area.textContent = result.area.toFixed(2);
    price.textContent = `$${result.price.toLocaleString('en-US')}`;
    lead.hidden = result.area <= pricing.largePieceArea;
    lead.textContent = `Made to order · ~${pricing.largePieceWeeks} weeks`;
    setProperty('Width_cm', width.value); setProperty('Height_cm', height.value); setProperty('Area_m2', result.area.toFixed(2));
    setProperty('Wool', `${woolPercentage(wool.value)}%`); setProperty('Complexity', pricing.complexity); setProperty('Computed_USD', result.price);
    setProperty('Frame', frame?.value ?? 'Unframed'); setProperty('Frame_premium_USD', framePremium);
  };
  [size, wool, frame, width, height].filter(Boolean).forEach((input) => input.addEventListener('input', update));
  [size, wool, frame].filter(Boolean).forEach((input) => input.addEventListener('change', update));
  const syncPricingChoice = (choice, value) => {
    const control = choice === 'size' ? size : choice === 'material' ? wool : choice === 'frame' ? frame : null;
    if (!control || !value || control.value === value) return;
    control.value = value;
    control.dispatchEvent(new Event('change'));
  };
  const openCustomSize = () => {
    size.value = 'Custom size';
    size.dispatchEvent(new Event('change'));
  };
  root.classList.add('is-pricing-picker-ready'); update();
  picker.closest('form').addEventListener('submit', async (event) => {
    if (!isCustom()) return;
    event.preventDefault(); update(); if (submit.disabled) return;
    submit.disabled = true;
    const computed = Number(property('Computed_USD').value);
    const ladder = selectLadderVariant((pricing.customVariants || []).map((variant) => ({ id: variant.id, price: variant.price / 100 })), computed);
    if (!ladder) { error.textContent = 'This size exceeds our $9,999 custom-price limit. Please contact us for a quote.'; submit.disabled = false; return; }
    const customPropertyNames = ['Artwork', 'Artwork_handle', 'Width_cm', 'Height_cm', 'Area_m2', 'Wool', 'Complexity', 'Computed_USD'];
    const submittedPropertyNames = frame
      ? [...customPropertyNames.slice(0, -1), 'Frame', 'Frame_premium_USD', customPropertyNames.at(-1)]
      : customPropertyNames;
    const properties = Object.fromEntries(submittedPropertyNames.map((name) => [`_${name}`, property(name).value]));
    try {
      const cartApi = window.HewnCartDrawer;
      if (!cartApi?.addItems) throw new Error('Cart drawer is unavailable.');
      submit.textContent = 'Adding…';
      await window.HewnCartDrawer.addItems({ items: [{ id: ladder.id, quantity: 1, properties }] }, submit);
      error.textContent = '';
    } catch (cartError) {
      error.textContent = cartError.message || 'We could not add this piece to your cart. Please try again.';
    } finally {
      submit.disabled = false;
      submit.textContent = 'Add to cart';
    }
  });
  return { syncPricingChoice, openCustomSize };
}

function placeAppSlots() {
  document.querySelectorAll('[data-pdp-app-slot-render]').forEach((slot) => {
    const target = document.querySelector(`[data-pdp-app-slot="${slot.dataset.pdpAppSlotRender}"]`);
    if (target && slot.children.length) target.replaceChildren(...slot.childNodes);
    if (target && slot !== target) slot.hidden = true;
  });
}

function initialiseAll() {
  placeAppSlots();
  document.querySelectorAll('[data-product-root]').forEach(initialiseProduct);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialiseAll);
  else initialiseAll();
  document.addEventListener('shopify:section:load', initialiseAll);
}
